import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
// Phase NOTARY-FOCUS N2 — Shared landing for /{locale}/notary
// Used by zh, ja, ko, ru, ar, de, fr locales. Pure presentation component
// driven by data from src/data/notary-i18n.ts.

import { Link } from "@tanstack/react-router";
import { ScrollText, ShieldCheck, MessageCircle, Phone, CheckCircle2 } from "lucide-react";
import type { NotaryLocale } from "@/data/notary-i18n";
import { NOTARY_LOCALES } from "@/data/notary-i18n";
import NotaryEmbassyWall from "@/components/NotaryEmbassyWall";
import NotaryQuickContact from "@/components/NotaryQuickContact";
import NotaryCompatChecker from "@/components/NotaryCompatChecker";
import NotaryPricingTable from "@/components/NotaryPricingTable";
import NotaryCaseStudies from "@/components/NotaryCaseStudies";
import NotaryAuthorByline from "@/components/NotaryAuthorByline";
import NotaryLiveTicker from "@/components/NotaryLiveTicker";
import RelatedNotaryLinks from "@/components/RelatedNotaryLinks";
import LocaleServiceLinks from "@/components/LocaleServiceLinks";


const CATEGORY_ORDER: Array<keyof NotaryLocale["categories"]> = [
  "signature", "affidavit", "copy", "corporate", "property", "intl",
];

export default function MultilingualNotaryLanding({ l }: { l: NotaryLocale }) {
  const rtl = l.dir === "rtl";
  return (
    <main dir={l.dir ?? "ltr"} className="min-h-screen bg-white" lang={l.locale}>
      <section className="relative bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/75">
            <Link to="/notary" className="hover:text-gold">Notary Public Thailand</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/90">{l.langName}</span>
          </nav>

          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ScrollText className="h-3.5 w-3.5" /> Notarial Services Attorney · Bangkok
          </div>

          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">{l.h1}</h1>
          <p data-speakable className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{l.hero}</p>

          {/* Key facts grid */}
          <dl className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {([
              [l.factsLabel.price, l.facts.price],
              [l.factsLabel.turnaround, l.facts.turnaround],
              [l.factsLabel.signer, l.facts.signer],
              [l.factsLabel.usedIn, l.facts.usedIn],
              [l.factsLabel.office, l.facts.office],
            ] as const).map(([k, v]) => (
              <div key={k} className="rounded-xl border border-white/15 bg-white/5 p-3">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{k}</dt>
                <dd className="mt-1 text-sm font-semibold text-white">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> {l.ctaConsult}
            </a>
            <a href="tel:0832494999"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> {l.ctaCall}
            </a>
          </div>

          {/* AEO Quick Answer — speakable Q/A microdata for AI Overview eligibility */}
          <div
            className="mt-8 rounded-2xl border border-gold/30 bg-white/[0.04] p-5 sm:p-6"
            itemScope
            itemType="https://schema.org/Question"
            data-speakable="true"
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-gold">AI Quick Answer</p>
            <h2 className="mt-1 text-base font-bold text-white sm:text-lg" itemProp="name">{l.h1}</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="mt-2 text-sm leading-relaxed text-white/85 sm:text-base" itemProp="text">{l.hero}</p>
            </div>
          </div>

          {/* Atomic / quotable stats — language-neutral numerics that AI pulls into Overview */}
          <ul className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3" data-speakable="true">
            {([
              [l.factsLabel.price, l.facts.price],
              [l.factsLabel.turnaround, l.facts.turnaround],
              [l.factsLabel.signer, l.facts.signer],
              [l.factsLabel.office, l.facts.office],
            ] as const).map(([k, v]) => (
              <li key={k} className="rounded-xl border border-white/10 bg-white/5 p-3 text-center">
                <div className="text-sm font-extrabold text-gold sm:text-base">{v}</div>
                <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-white/80">{k}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Services / 9 categories */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{l.servicesH2}</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORY_ORDER.map((cat) => (
            <div key={cat} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <ShieldCheck className="h-6 w-6 text-amber-600" />
              <p className="mt-3 font-display text-base font-extrabold text-navy-deep">{l.categories[cat]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="bg-slate-50 px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{l.stepsH2}</h2>
          <ol className={`mt-6 grid gap-4 sm:grid-cols-2 ${rtl ? "" : ""}`}>
            {l.steps.map((s) => (
              <li key={s.title} className="rounded-2xl border border-slate-200 bg-white p-5">
                <p className="font-display text-base font-extrabold text-navy-deep">{s.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{l.faqH2}</h2>
        <div className="mt-6 space-y-3">
          {l.faqs.map((f, i) => (
            <details key={i} className="group rounded-2xl border border-slate-200 bg-white p-5 open:shadow-sm">
              <summary className="cursor-pointer list-none font-display text-base font-extrabold text-navy-deep">
                <span className="mr-2 text-amber-600">Q.</span>{f.q}
              </summary>
              <p data-speakable className="voice-faq-answer mt-3 text-sm leading-relaxed text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Cross-locale links + nudge */}
      <section className="bg-navy-deep px-4 py-12 text-white">
        <div className="mx-auto max-w-5xl">
          <p className="text-sm text-white/85">{l.bottomNudge}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a href="https://line.me/ti/p/@nycli" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold">
              <MessageCircle className="h-3.5 w-3.5" /> {l.contactLine}
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-xs font-bold text-navy-deep">
              <Phone className="h-3.5 w-3.5" /> {l.contactPhone}
            </a>
          </div>

          <div className="mt-8 border-t border-white/15 pt-6">
            <p className="text-[11px] font-bold uppercase tracking-widest text-gold">Other languages</p>
            <ul className="mt-3 flex flex-wrap gap-2 text-xs">
              <li><Link to="/notary" className="rounded-full bg-white/10 px-3 py-1 hover:bg-white/20">🇹🇭 ภาษาไทย</Link></li>
              <li><Link to="/en/notary" className="rounded-full bg-white/10 px-3 py-1 hover:bg-white/20">🇺🇸 English</Link></li>
              {NOTARY_LOCALES.filter((o) => o.locale !== l.locale).map((o) => (
                <li key={o.locale}>
                  <a href={o.path} className="rounded-full bg-white/10 px-3 py-1 hover:bg-white/20">
                    {o.langName}
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-5 flex items-start gap-2 text-[12px] text-white/70">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-300" />
              All notarial acts signed in person by a court-licensed Notarial Services Attorney registered with the Lawyers Council of Thailand. DBD 0435567000061.
            </p>
          </div>
        </div>
      </section>

      <NotaryLiveTicker lang={l.locale} />
      <NotaryAuthorByline lang={l.locale} />
      <NotaryPricingTable lang={l.locale} />
      <NotaryCaseStudies lang={l.locale} />
      <NotaryCompatChecker lang={l.locale as never} />
      <NotaryEmbassyWall lang={l.locale} />
      <NotaryQuickContact lang={l.locale} />
      <RelatedNotaryLinks context="hub" lang={l.locale} />

          <ServicePageEnhancer serviceName="Notary Public" locale={l.locale as never} />
<RelatedHubGrid locale="en" />      <LocaleServiceLinks />
</main>
  );
}
